import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Link,
  Stack,
  Alert
} from '@customink/pigment-react';
import ContentCard from '~/components/ContentCard';
import NumberInput from '~/shared/components/NumberInput';

import {useDispatch, useSelector} from 'react-redux';
import {getPostalCode, getTotalQuantity} from '~/selectors/QuoteSelectors';
import {showShippingForm, updatePostalCode} from '~/actions/QuoteActions';
import {setEstimatedQty, getEstimatedQty, setIntl} from '~/slices/priceSlice';

import {getZipCodeCookie} from '~/utils/user';
import {pushGtmEvent} from '~/utils/analytics';
import {validUSorCanadianPostalCode} from '~/utils/quote';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export default function ZipQuantity({onClose}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const zipCodeGuess = useSelector(getPostalCode) || getZipCodeCookie();
  // use saved design quantity if it exists,
  // otherwise use estimated quantity entered by the user
  const savedDesignQty = useSelector(getTotalQuantity);
  const estimatedQtyForSimplePricing = useSelector(getEstimatedQty);
  const estQty = savedDesignQty || estimatedQtyForSimplePricing;

  const numberInputRef = useRef(null);

  const [quantity, setQuantity] = useState(estQty);
  const [zipCode, setZipCode] = useState(zipCodeGuess);
  const [zipError, setZipError] = useState(false);
  const [qtyError, setQtyError] = useState(false);

  // a hacky way to avoid using autoFocus but wait for the animation to finish
  // setting focus any earlier will jank the slide out animation
  useEffect(() => {
    if (numberInputRef.current) {
      setTimeout(() => numberInputRef.current.focus(), 500);
    }
  }, [numberInputRef]);

  // On Load analytics event
  useEffect(() => {
    pushGtmEvent({
      event: 'lab_pricing_start',
      pageTitle: 'NDX Pricing Form',
      pageURL: 'ndx/quote/form' // completely made up, as usual
    });
  }, []);

  const onProceed = () => {
    const isValid = validate();
    if (!isValid) return;

    dispatch(updatePostalCode(zipCode));
    // i.e. don't show size/quantities form
    dispatch(showShippingForm(false)); // I can't believe this was hiding in the quote state
    dispatch(setEstimatedQty(quantity));
    navigate('/next/price');
  };

  const validate = () => {
    let valid = true;
    if (!quantity) {
      setQtyError(true);
      valid = false;
    }
    if (!zipCode || !validUSorCanadianPostalCode(zipCode)) {
      setZipError(true);
      valid = false;
    }
    return valid;
  };

  const onQtyChange = value => {
    setQtyError(false);
    setQuantity(value);
  };

  const onZipCodeChange = e => {
    setZipError(false);
    setZipCode(e.target.value);
  };

  const onIntlClick = () => {
    pushGtmEvent({
      event: 'interaction',
      interaction_category: 'lab_pricing_start',
      interaction_action: 'click international link'
    });
    dispatch(setIntl(true));
    navigate('/next/quote');
  };

  const onBack = () => {
    pushGtmEvent({
      event: 'interaction',
      interaction_category: 'lab_pricing_start',
      interaction_action: 'click back button'
    });
    onClose();
  };

  const onExit = () => {
    pushGtmEvent({
      event: 'interaction',
      interaction_category: 'lab_pricing_start',
      interaction_action: 'close'
    });
    onClose();
  };

  return (
    <ContentCard onNavBack={onBack} onNavClose={onExit} title="Pricing">
      <Stack
        sx={{
          padding: '1.5rem',
          alignItems: 'center',
          width: '100%',
          gap: '1.5rem'
        }}>
        {/* Errors */}
        {zipError && (
          <Alert sx={{width: '100%'}} severity="error">
            Please enter a valid ZIP code
          </Alert>
        )}
        {qtyError && (
          <Alert sx={{width: '100%'}} severity="error">
            Please enter your quantity
          </Alert>
        )}

        {/* Qty Input */}
        <Stack sx={{gap: '1rem', alignItems: 'center'}}>
          <Typography
            sx={{fontSize: '24px', fontWeight: '700', lineHeight: '125%'}}>
            How many do you need?
          </Typography>
          <NumberInput
            data-testid="zip-quantity-qty"
            name="est-qty"
            value={quantity}
            onChange={onQtyChange}
            error={qtyError}
            incrementers
            ref={numberInputRef}
          />
        </Stack>

        {/* Zip Input */}
        <Stack alignItems={'center'} gap={'8px'}>
          <Box sx={{display: 'flex', gap: '8px', alignItems: 'center'}}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                lineHeight: '125%'
              }}>
              Where do we send it?
            </Typography>
            <TextField
              data-testid="zip-quantity-zip"
              name="zipCode"
              value={zipCode}
              placeholder="ZIP Code"
              type="text"
              onChange={onZipCodeChange}
              removeHelperTextSpace
              error={zipError}
              inputProps={{sx: {width: '94px', marginTop: 0}}}
            />
          </Box>
          <Link
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              lineHeight: '150%',
              textDecoration: 'underline'
            }}
            onClick={onIntlClick}>
            Outside the U.S. or Canada?
          </Link>
        </Stack>

        {/* CTA */}
        <Button
          size="large"
          onClick={onProceed}
          data-testid="zip-quantity-proceed">
          See Your Price
        </Button>
      </Stack>
    </ContentCard>
  );
}

const propTypes = {
  onClose: PropTypes.func
};

ZipQuantity.propTypes = propTypes;
